import { WorkoutRecord } from '../../../lib/workoutRecord';
import WorkoutExerciseCard from './WorkoutExerciseCard';

type MediaClick = (mediaList: any[], clickedIndex: number) => void;

/** 운동 기록 섹션 헤더 — "🏋️ 운동 기록" + 운동 수/총 시간 */
export const WorkoutSectionHeader = ({
    exerciseCount,
    durationMinutes,
}: {
    exerciseCount?: number;
    durationMinutes?: number | null;
}) => (
    <div className="flex items-center gap-[8px] mb-[12px]">
        <span className="text-[13px] font-semibold text-[#1F2937]">🏋️ 운동 기록</span>
        {!!exerciseCount && (
            <span className="text-[11px] text-[#3B82F6] bg-[#EFF3FB] rounded-[6px] px-[7px] py-[1px]">
                운동 {exerciseCount}개
            </span>
        )}
        {!!durationMinutes && durationMinutes > 0 && (
            <span className="text-[11px] text-[#64748B]">· {durationMinutes}분</span>
        )}
    </div>
);

/** 회원 리캡 상단 요약 (summaryText) */
export const WorkoutSummaryBlock = ({ text }: { text: string }) => (
    <div className="rounded-[8px] bg-white border border-solid border-[#E2E8F0] px-[12px] py-[10px] mb-[12px]">
        <p className="text-[13px] text-[#374151] leading-[20px] whitespace-pre-wrap">{text}</p>
    </div>
);

/** 수업 마지막 메시지 (closingMessage) */
export const WorkoutClosingBlock = ({ text }: { text: string }) => (
    <div className="mt-[12px] rounded-[8px] bg-[#FFFBEB] border border-solid border-[#FDE68A] px-[12px] py-[10px]">
        <span className="text-[11px] font-semibold text-[#B45309]">마지막 메시지</span>
        <p className="text-[13px] text-[#78350F] leading-[20px] mt-[2px] whitespace-pre-wrap">{text}</p>
    </div>
);

type Props = {
    workoutRecord: WorkoutRecord;
    onMediaClick?: MediaClick;
};

/**
 * 번들 모드: contents에 WORKOUT_EXERCISE 마커가 없을 때(현재 대부분) 운동 기록 전체를
 * 하나의 섹션으로 표시. 요약 → 운동 카드들 → 마지막 메시지.
 */
const WorkoutRecordView = ({ workoutRecord, onMediaClick }: Props) => {
    const { summaryText, closingMessage, exercises, durationMinutes } = workoutRecord;

    return (
        <div className="mb-[16px] rounded-[12px] bg-[#F8FAFC] border border-solid border-[#E2E8F0] p-[16px]">
            <WorkoutSectionHeader exerciseCount={exercises.length} durationMinutes={durationMinutes} />

            {summaryText && <WorkoutSummaryBlock text={summaryText} />}

            {exercises.length > 0 && (
                <div className="flex flex-col gap-[10px]">
                    {exercises.map((exercise, index) => (
                        <WorkoutExerciseCard
                            key={`exercise-${exercise.order}-${index}`}
                            exercise={exercise}
                            displayNumber={index + 1}
                            onMediaClick={onMediaClick}
                        />
                    ))}
                </div>
            )}

            {closingMessage && <WorkoutClosingBlock text={closingMessage} />}
        </div>
    );
};

export default WorkoutRecordView;
